import React, { useState, useEffect } from 'react';
import { getAllBureaux, createBureau } from '../services/api';

const Bureaux = () => {
  const [bureaux, setBureaux] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const initialFormState = {
    nom: '',
    code: '',
    responsable: '',
    telephone: '',
    email: '',
    adresse: '',
    description: '',
    statut: 'actif',
  };
  const [showForm, setShowForm] = useState(false);
  const [newBureau, setNewBureau] = useState(initialFormState);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadBureaux();
  }, []);

  const loadBureaux = async () => {
    setLoading(true);
    try {
      const data = await getAllBureaux();
      setBureaux(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des bureaux:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des bureaux' });
      setBureaux([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculs de pagination
  const totalPages = Math.ceil((Array.isArray(bureaux) ? bureaux.length : 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBureaux = Array.isArray(bureaux) ? bureaux.slice(startIndex, endIndex) : [];

  // Gestion de la pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [bureaux.length]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewBureau((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateBureau = async (e) => {
    e.preventDefault();
    if (!newBureau.nom.trim()) {
      setMessage({ type: 'error', text: 'Le nom du bureau est requis.' });
      return;
    }

    setCreating(true);
    setMessage({ type: '', text: '' });
    try {
      await createBureau(newBureau);
      setMessage({ type: 'success', text: 'Bureau ajouté avec succès.' });
      setNewBureau(initialFormState);
      setShowForm(false);
      await loadBureaux();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erreur lors de la création du bureau.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-8 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <h1 className="text-3xl font-bold text-text flex items-center">
            <i className="fas fa-building mr-3 text-primary"></i>
            Gestion des Bureaux
          </h1>
          <button
            type="button"
            className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-white rounded-lg font-medium shadow-md hover:bg-primary-dark transition-all"
            onClick={() => setShowForm((prev) => !prev)}
          >
            <i className="fas fa-plus-circle text-lg"></i>
            {showForm ? 'Fermer le formulaire' : 'Nouveau Bureau'}
          </button>
        </div>

        {showForm && (
          <div className="mb-8 bg-cardBg rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-text mb-4 flex items-center">
              <i className="fas fa-clipboard-list mr-2 text-primary"></i>
              Nouveau Bureau
            </h2>
            <form onSubmit={handleCreateBureau} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text mb-2">Nom *</label>
                <input
                  type="text"
                  name="nom"
                  value={newBureau.nom}
                  onChange={handleFormChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Code</label>
                <input
                  type="text"
                  name="code"
                  value={newBureau.code}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Responsable</label>
                <input
                  type="text"
                  name="responsable"
                  value={newBureau.responsable}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Téléphone</label>
                <input
                  type="text"
                  name="telephone"
                  value={newBureau.telephone}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={newBureau.email}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Statut</label>
                <select
                  name="statut"
                  value={newBureau.statut}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                >
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text mb-2">Adresse</label>
                <textarea
                  name="adresse"
                  value={newBureau.adresse}
                  onChange={handleFormChange}
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text mb-2">Description</label>
                <textarea
                  name="description"
                  value={newBureau.description}
                  onChange={handleFormChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setNewBureau(initialFormState);
                  }}
                  className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-5 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>Enregistrement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>Enregistrer
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Message */}
        {message.text && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-100 text-green-700 border border-green-400'
                : 'bg-red-100 text-red-700 border border-red-400'
            }`}
          >
            <i
              className={`fas ${
                message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
              } mr-2`}
            ></i>
            {message.text}
          </div>
        )}

        {/* Table */}
        <div className="bg-cardBg rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="px-6 py-4 text-left">ID</th>
                  <th className="px-6 py-4 text-left">Nom</th>
                  <th className="px-6 py-4 text-left">Code</th>
                  <th className="px-6 py-4 text-left">Responsable</th>
                  <th className="px-6 py-4 text-left">Téléphone</th>
                  <th className="px-6 py-4 text-left">Email</th>
                  <th className="px-6 py-4 text-left">Statut</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center">
                      <i className="fas fa-spinner fa-spin text-2xl text-primary"></i>
                    </td>
                  </tr>
                ) : !Array.isArray(bureaux) || bureaux.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-text-light">
                      Aucun bureau trouvé
                    </td>
                  </tr>
                ) : (
                  currentBureaux.map((bureau) => (
                    <tr key={bureau.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">{bureau.id}</td>
                      <td className="px-6 py-4">{bureau.nom || '-'}</td>
                      <td className="px-6 py-4">{bureau.code || '-'}</td>
                      <td className="px-6 py-4">{bureau.responsable || '-'}</td>
                      <td className="px-6 py-4">{bureau.telephone || '-'}</td>
                      <td className="px-6 py-4">{bureau.email || '-'}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            bureau.statut === 'actif'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {bureau.statut === 'actif' ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {Array.isArray(bureaux) && bureaux.length > 0 && totalPages > 1 && (
          <div className="mt-6 bg-cardBg rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-text-light">
                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(endIndex, bureaux.length)}</strong> sur <strong>{bureaux.length}</strong> bureau(x)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-4 py-2 rounded-lg transition-all ${
                            currentPage === page
                              ? 'bg-primary text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span key={page} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {Array.isArray(bureaux) && bureaux.length > 0 && (
          <div className="mt-4 bg-cardBg rounded-lg shadow-lg p-4">
            <p className="text-text-light text-sm">
              <i className="fas fa-info-circle mr-2 text-primary"></i>
              Total: <strong>{bureaux.length}</strong> bureau(x)
              {totalPages > 1 && (
                <span className="ml-4">
                  | Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bureaux;

